import React, { useEffect, useState } from "react";
import { sandboxAdminApi } from "../../../services/phase1Api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { FlaskConical, Loader2, Search, UserCheck, ShieldCheck } from "lucide-react";

const ROLE_LABEL = {
  portfolio_owner: "Portfolio Manager", sales: "Sales", school_user: "School User",
  tenant_user: "School User", client: "Client",
};

export default function SandboxAccessPage({ session }) {
  const [access, setAccess] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState("");

  const loadAccess = () => sandboxAdminApi.listAccess(session)
    .then((d) => setAccess(d.access || [])).catch(() => {}).finally(() => setLoading(false));

  useEffect(() => { loadAccess(); }, []); // eslint-disable-line

  const search = async () => {
    setSearching(true);
    try { const d = await sandboxAdminApi.candidates(q, session); setResults(d.candidates || []); }
    catch { toast.error("Search failed"); }
    setSearching(false);
  };

  const toggle = async (email, enabled) => {
    setBusy(email);
    try {
      await sandboxAdminApi.setAccess(email, enabled, session);
      toast.success(enabled ? `Sandbox enabled for ${email}` : `Sandbox disabled for ${email}`);
      loadAccess();
    } catch { toast.error("Update failed"); }
    setBusy("");
  };

  const enabledEmails = new Set(access.filter((a) => a.enabled).map((a) => a.email));

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="sandbox-access-page">
      <div className="flex items-center gap-3 mb-1">
        <div className="h-10 w-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center"><FlaskConical className="h-5 w-5" /></div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Sandbox Access</h1>
          <p className="text-sm text-foreground/50">Choose which pilot users can generate their own sample test data.</p>
        </div>
      </div>

      {/* who has access */}
      <div className="mt-6 rounded-xl border border-border bg-card p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-foreground/50 mb-3 flex items-center gap-1"><UserCheck className="h-3.5 w-3.5" /> Users with access</div>
        {loading ? (
          <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-foreground/40" /></div>
        ) : access.length === 0 ? (
          <div className="text-sm text-foreground/40 py-3">No users enabled yet. Search below to grant access.</div>
        ) : (
          <div className="space-y-1" data-testid="sandbox-access-list">
            {access.map((a) => (
              <div key={a.email} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-muted">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">{a.name || a.email}</div>
                  <div className="text-[11.5px] text-foreground/45">{a.email} · {ROLE_LABEL[a.role] || a.role || "User"}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[11.5px] font-semibold ${a.enabled ? "text-emerald-600" : "text-foreground/40"}`}>{a.enabled ? "Enabled" : "Disabled"}</span>
                  <Switch checked={!!a.enabled} disabled={busy === a.email}
                    onCheckedChange={(v) => toggle(a.email, v)} data-testid={`sandbox-switch-${a.email}`} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* find users */}
      <div className="mt-4 rounded-xl border border-border bg-card p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-foreground/50 mb-3 flex items-center gap-1"><Search className="h-3.5 w-3.5" /> Find a user to enable</div>
        <div className="flex gap-2">
          <Input placeholder="Search by name or email…" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()} data-testid="sandbox-search-input" />
          <Button onClick={search} disabled={searching} data-testid="sandbox-search-btn">
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </div>
        <div className="mt-3 space-y-1" data-testid="sandbox-search-results">
          {results.map((r) => {
            const on = enabledEmails.has(r.email);
            return (
              <div key={r.email} className="flex items-center justify-between px-3 py-2 rounded-lg hover:bg-muted">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">{r.name || r.email}</div>
                  <div className="text-[11.5px] text-foreground/45">{r.email} · {ROLE_LABEL[r.role] || r.role || "User"}</div>
                </div>
                <Button size="sm" variant={on ? "outline" : "default"} className={on ? "text-red-500" : ""}
                  disabled={busy === r.email} onClick={() => toggle(r.email, !on)} data-testid={`sandbox-grant-${r.email}`}>
                  {busy === r.email ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (on ? "Disable" : "Enable")}
                </Button>
              </div>
            );
          })}
          {results.length === 0 && q && !searching && <div className="text-sm text-foreground/40 py-2">No matches. Try a different name or email.</div>}
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2 text-[11.5px] text-foreground/50 bg-muted rounded-lg px-3 py-2">
        <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>Enabled users see a "Testing Sandbox" panel inside their own portal. All data they generate is tagged as test data — excluded from billing, commission and admin reports — and each user can only see and clear their own.</span>
      </div>
    </div>
  );
}
